import { Contact } from 'types/contacts.ts'
import { NormalizedEntities } from 'store/contact/types.ts'
import { memoize } from 'proxy-memoize'
import { GetContactSuccessPayload } from 'store/contact/thunks/types.ts'
import { getAccountsWithActiveElectricityContract, getLastOpenedContract, setLastOpenedContract } from 'utils/contracts.ts'
import { UrlSearchParamsKeys } from 'store/app/types.ts'
import { store } from 'store/index.ts'
import { log } from 'utils/logging.ts'
import { Flow } from 'types/logging.ts'

/**
 * Ignore gas only contracts
 * @param {Contact} contact
 * @returns {{ignoredBillingContracts: string[], updatedContact?: Contact}}
 */
export const ignoreGasOnlyContracts = (
  contact: Contact
): {
  ignoredBillingContracts: string[]
  updatedContact?: Contact
} => {
  const ignoredBillingContracts = new Set<string>()
  let updatedContact: Contact | undefined = undefined

  contact.accounts?.forEach((account) => {
    account.billingContracts?.forEach((contract) => {
      if (!contract.serviceContracts?.electricity) {
        ignoredBillingContracts.add(contract.contractNumber)
      }
    })
  })

  // Update the contact data to remove the ignored contracts
  if (ignoredBillingContracts.size > 0) {
    updatedContact = {
      ...contact,
      accounts: contact.accounts?.map((account) => ({
        ...account,
        billingContracts: account.billingContracts?.filter((contract) => !ignoredBillingContracts.has(contract.contractNumber))
      }))
    }
  }

  return { ignoredBillingContracts: Array.from(ignoredBillingContracts), updatedContact }
}

/**
 * Process contact data for getMyContact & getContactByEmail thunks
 * @param {Contact} contact
 * @param {GetContactSuccessPayload['selected']} cachedSelection - Comes from redux-remember (src/store/index.ts), saved in session storage.
 * @returns {GetContactSuccessPayload}
 */
export const processContact = (
  contact: Contact,
  cachedSelection?: GetContactSuccessPayload['selected']
): Omit<GetContactSuccessPayload, 'ignoredBillingContracts'> => {
  // Normalize data for store
  const normalizedStoreData = memoizedNormalizeContactData(contact)
  const { accounts, billingContracts } = normalizedStoreData

  // Select the requested customer or billing contract if necessary
  const requestedCustomerId = store.getState().app.urlSearchParams[UrlSearchParamsKeys.CUSTOMER_ID]
  const requestedBillingContractId = store.getState().app.urlSearchParams[UrlSearchParamsKeys.BILLING_CONTRACT_NUMBER]

  // If a specific customer id is requested in the URL search params, select that account
  if (requestedCustomerId && requestedCustomerId in accounts) {
    const account = accounts[requestedCustomerId]

    if (!account) {
      log({
        error: `Account with requested customer number ${requestedCustomerId} not found`,
        identifier: `[${Flow.CONTACTS}:processContactWithRequestedId]`
      })
    }

    // Open last opened contract of requested account if available
    const lastOpenedContract = getLastOpenedContract(account.customerNumber)

    const hasLastOpenedContract =
      !!lastOpenedContract && account.billingContracts.some((contractNumber) => contractNumber === lastOpenedContract)
    const billingContract = hasLastOpenedContract ? billingContracts[lastOpenedContract] : billingContracts[account.billingContracts[0]]

    if (!hasLastOpenedContract) {
      setLastOpenedContract(account.customerNumber, billingContract.contractNumber)
    }

    if (account && billingContract) {
      return {
        normalizedStoreData,
        selected: {
          account: account.customerNumber,
          billingContract: billingContract.contractNumber,
          opportunity: billingContract?.opportunitySfId
        }
      } satisfies Omit<GetContactSuccessPayload, 'ignoredBillingContracts'>
    }
  }

  // If a specific billing contract number is given in the URL search params, select that contract
  if (requestedBillingContractId && requestedBillingContractId in billingContracts) {
    const selectedBillingContract = billingContracts[requestedBillingContractId]
    setLastOpenedContract(selectedBillingContract.customerNumber, selectedBillingContract.contractNumber)

    return {
      normalizedStoreData,
      selected: {
        account: selectedBillingContract.customerNumber,
        billingContract: selectedBillingContract.contractNumber
      }
    }
  }

  const accountsWithActiveElectricityContract = getAccountsWithActiveElectricityContract(billingContracts)

  // If only 1 contract is found OR there is only a single active electricity contract, select that contract
  if (Object.keys(billingContracts).length === 1 || accountsWithActiveElectricityContract.size === 1) {
    const previouslySelectedItem =
      cachedSelection?.billingContract && cachedSelection?.billingContract in billingContracts
        ? billingContracts[cachedSelection.billingContract]
        : undefined
    const activeBillingContracts = Array.from(accountsWithActiveElectricityContract).flatMap(([_, contract]) => contract)
    const firstContract = Object.values(billingContracts)[0]
    const selectedContract = previouslySelectedItem || activeBillingContracts?.[0] || firstContract

    return {
      normalizedStoreData,
      selected: {
        account: selectedContract.customerNumber,
        billingContract: selectedContract.contractNumber
      }
    } satisfies Omit<GetContactSuccessPayload, 'ignoredBillingContracts'>
  }

  return {
    normalizedStoreData,
    selected: cachedSelection?.billingContract && cachedSelection?.billingContract in billingContracts ? cachedSelection : undefined
  } satisfies Omit<GetContactSuccessPayload, 'ignoredBillingContracts'>
}

/**
 * Convert API response to normalized store entities
 * @param {Contact} contact
 * @returns {NormalizedEntities}
 */
export const normalizeContactData = (contact: Contact): NormalizedEntities => {
  // Normalize data for store
  const normalizedContact: NormalizedEntities['contact'] = {
    ...contact,
    accounts: contact?.accounts?.map((account) => account.customerNumber) || []
  }
  const accounts: NormalizedEntities['accounts'] = {}
  const billingContracts: NormalizedEntities['billingContracts'] = {}
  const opportunities: NormalizedEntities['opportunities'] = {}

  contact?.accounts?.forEach((account) => {
    const { customerNumber } = account

    accounts[customerNumber] = {
      ...account,
      billingContracts: account.billingContracts?.map((contract) => contract.contractNumber) || [],
      opportunities: account.opportunities?.map((opportunity) => opportunity.sfId) || []
    }

    account.billingContracts?.forEach((contract) => {
      billingContracts[contract.contractNumber] = contract
    })

    account.opportunities?.forEach((opportunity) => {
      opportunities[opportunity.sfId] = opportunity
    })
  })

  return {
    contact: normalizedContact,
    accounts,
    billingContracts,
    opportunities
  }
}

/**
 * Memoized version of normalizeContactsData
 */
export const memoizedNormalizeContactData = memoize<Contact, NormalizedEntities>(normalizeContactData)
